const GUID_REGEX_PATTERN = '[A-Fa-f0-9]{8}-(?:[A-Fa-f0-9]{4}-){3}[A-Fa-f0-9]{12}';

/**
 * Generates a base64-encoded GUID using browser crypto APIs.
 *
 * Note: By design, this function produces a guid in a different format from backend GUIDs.
 * This avoids confusing frontend-generated IDs with backend-generated GUIDs.
 *
 * @example dcWyqxUedy-c5owG_HIlsA
 * @returns The generated identifier.
 */
export function base64guid(): string {
  const array = new Uint8Array(16); // 128 bits of randomness
  window.crypto.getRandomValues(array);
  return (
    window
      .btoa(String.fromCharCode.apply(null, Array.from(array)))
      // Remove url unsafe characters and padding
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')
  );
}

/**
 * Validates that a value is a GUID in the canonical 8-4-4-4-12 hexadecimal format.
 *
 * @param guid - The GUID to check
 * @example af8a8416-6e18-a307-bd9c-f2c947bbb3aa
 * @returns `true` if the value is a valid GUID, otherwise `false`.
 */
export function validateGuid(guid: unknown): guid is string {
  const re = new RegExp(`^${GUID_REGEX_PATTERN}$`);
  return typeof guid === 'string' && re.test(guid);
}

/**
 * Extracts all GUID substrings from a string.
 *
 * @param s - The string from which to extract
 * @returns Array of GUIDs
 */
export function extractGuids(s: string): RegExpMatchArray | string[] {
  const re = new RegExp(GUID_REGEX_PATTERN, 'g');
  return `${s}`.match(re) || [];
}

/**
 * Generates a random UUID v4 using the browser crypto API, with a fallback implementation when needed.
 *
 * @example af8a8416-6e18-a307-bd9c-f2c947bbb3aa
 * @returns A UUID string in canonical 8-4-4-4-12 format.
 */
export function randomUUID(): string {
  // Safety check because of the warning about only being available in HTTPS context on some browsers according to MDN:
  // https://developer.mozilla.org/en-US/docs/Web/API/Crypto/randomUUID#browser_compatibility
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return fallbackRandomUUID();
}

/**
 * Generates a UUID v4-style identifier without relying on `crypto.randomUUID()`.
 *
 * This exists as a compatibility fallback for environments where `crypto.randomUUID()` is unavailable.
 *
 * @returns A UUID string in canonical 8-4-4-4-12 format.
 */
export function fallbackRandomUUID(): string {
  let timestamp = Date.now();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const randomNibble = ((timestamp + Math.random() * 16) % 16) | 0;
    timestamp = Math.floor(timestamp / 16);
    return (char === 'x' ? randomNibble : (randomNibble & 0x3) | 0x8).toString(16);
  });
}
